import React, { useState } from 'react';

import { trackGAEvent } from '@/utility/analytics';
import { ACTIVITIY_EVENTS } from '@/utility/analyticsConsts';
import { t } from '@/utility/localization';

import FilterDropdown from '@/components/common/FilterDropdown';
import InfiniteList from '@/components/common/InfiniteList';
import Icon from '@/components/npl/Icon';

import ActivityRow from './components/ActivityRow';
import getConstants from './constants';
import useActivityFeed from './hooks/useActivityFeed';

function ActivityFeed({ activeCommunityId, activeCommunity }) {
  const { filterOptions } = getConstants();
  const [filter, setFilter] = useState(filterOptions[0]);

  const {
    loadingActivities,
    loadingMoreActivities,
    fetchActiveCommunityActivityData,
    fetchMoreActivities,
    activities,
    activitiesFullyRendered
  } = useActivityFeed({ activeCommunityId, activeCommunity });

  const isLoadingAnyActivities =
    loadingActivities || loadingMoreActivities;

  const handleFilterChange = (option) => {
    setFilter(option);
    fetchActiveCommunityActivityData({
      activityTypes: option?.value
    });
  };

  // only show user have reached the end if user have to scroll at least once, and all activities are fully rendered
  const userHaveLoadedAllActivities =
    activities.length > 20 && activitiesFullyRendered;

  const renderAcitivityRowSkeleton = () =>
    Array(2)
      .fill(null)
      .map((index) => (
        <div key={index}>
          <div className="flex items-center justify-between gap-8 px-8 py-12">
            <div className="flex items-center gap-8">
              <div className="h-32 w-32 animate-pulse rounded-full bg-npl-neutral-light-solid-6"></div>
              <div className="h-16 w-[240px] animate-pulse rounded-4 bg-npl-neutral-light-solid-6"></div>
            </div>
            <div className="h-16 w-[80px] animate-pulse rounded-4 bg-npl-neutral-light-solid-6"></div>
          </div>
          <div className="flex items-center justify-between gap-8 px-8 py-12">
            <div className="flex items-center gap-8">
              <div className="h-32 w-32 animate-pulse rounded-full bg-npl-neutral-light-solid-6"></div>
              <div className="h-16 w-[300px] animate-pulse rounded-4 bg-npl-neutral-light-solid-6"></div>
            </div>
            <div className="h-16 w-[60px] animate-pulse rounded-4 bg-npl-neutral-light-solid-6"></div>
          </div>
        </div>
      ));

  return (
    <div className="mb-[200px]">
      <div className="mt-32 flex items-center justify-between">
        <div className="text-heading-sm font-semibold">
          {t('activity')}
        </div>
        <div>
          <FilterDropdown
            options={filterOptions}
            selected={filter}
            title=""
            customClassNames={'max-h-[32px]'}
            onChange={({ option }) => handleFilterChange(option)}
            placementRight={true}
          />
        </div>
      </div>
      {activities?.length > 0 && (
        <div className="mt-20">
          <InfiniteList
            items={activities}
            isLoading={isLoadingAnyActivities}
            hasMore={!activitiesFullyRendered}
            loadMore={() => {
              const lastActivity = activities[activities?.length - 1];
              const lastActivityObjectId = lastActivity?.activityObjectId;

              fetchMoreActivities({
                lastActivityObjectId,
                activityTypes: filter?.value
              });
              trackGAEvent(ACTIVITIY_EVENTS.SCROLLING_ACTIVITIES);
            }}
            renderSkeleton={renderAcitivityRowSkeleton}>
            {(activity) => (
              <ActivityRow
                activity={activity}
                key={activity?.activityObjectId}
              />
            )}
          </InfiniteList>
        </div>
      )}
      {activities?.length === 0 && !loadingActivities && (
        <div className="mt-[50px] flex flex-col items-center">
          <Icon name="bell-01" width={40} height={40} fill="#C8C7C1" />
          <div className="mt-12 max-w-[154px] text-center text-label-lg font-medium text-npl-text-icon-on-light-surface-secondary/65">
            {t('no-activity-logged-try-another-filter')}
          </div>
        </div>
      )}
      {userHaveLoadedAllActivities && (
        <div className="relative mt-24 flex items-center">
          <div className="flex-grow border-t border-npl-neutral-light-solid-4"></div>
          <span className="mx-24 flex-shrink text-center text-label-sm text-npl-text-icon-on-light-surface-tertiary/50">
            {t('youve-reached-the-end-congratulations')}
          </span>
          <div className="flex-grow border-t border-npl-neutral-light-solid-4"></div>
        </div>
      )}
    </div>
  );
}

export default ActivityFeed;
